/**
 * ZoneAutocomplete — reusable zone/geo autocomplete input.
 *
 * Extracted per §4.4 of the Conversations design: one component, one debounce,
 * one abort, five field states:
 *   - idle (< 4 chars or untouched)
 *   - searching
 *   - results
 *   - no results ("aucun lieu ne correspond" — the user's problem)
 *   - unavailable ("recherche indisponible" — ours)
 *
 * A selection already made is NOT cleared when the user starts typing again
 * (the user can clear explicitly by emptying the field).
 *
 * The attribution string is a licence condition and MUST render under the list.
 *
 * A query under TARGET_LIMITS.geoAutocompleteMinLength (4) never reaches the API.
 * A 422 from zone validation shows no toast — callers handle errors.
 */
import { useRef, useState, useCallback } from 'react';
import {
  Box,
  CircularProgress,
  InputBase,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { TARGET_LIMITS } from '@traaker/shared';
import { api } from '../../services/api';
import type { GeoSuggestion } from '../../pages/targets/useZoneAutocomplete';

// ---------------------------------------------------------------------------
// Re-export the type for callers who don't import from the Targets hook directly.
// ---------------------------------------------------------------------------

export type { GeoSuggestion };

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ZoneAutocompleteProps {
  /** Current input query. */
  query: string;
  /** The picked suggestion, or null. */
  selected: GeoSuggestion | null;
  /** Called on every keystroke (after debounce is wired internally). */
  onQueryChange: (v: string) => void;
  /** Called when the user picks a suggestion. */
  onSelect: (s: GeoSuggestion) => void;
  /** Placeholder text for the input. */
  placeholder?: string;
  /** Language tag forwarded to the autocomplete API (`lang` param). Default: `fr`. */
  lang?: string;
  /** Called when Enter is pressed. Parent uses this to trigger "Ajouter". */
  onEnter?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const MIN_LEN = TARGET_LIMITS.geoAutocompleteMinLength;

export function ZoneAutocomplete({
  query,
  selected: _selected,
  onQueryChange,
  onSelect,
  placeholder = 'Ajouter une zone : pays, région, ville… (4 caractères minimum)',
  lang = 'fr',
  onEnter,
}: ZoneAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<GeoSuggestion[]>([]);
  const [attribution, setAttribution] = useState('© Geoapify');
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [open, setOpen] = useState(false);

  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastFetchedRef = useRef('');

  const fetchZone = useCallback(async (q: string) => {
    if (q.length < MIN_LEN) {
      setSuggestions([]);
      setOpen(false);
      setLoading(false);
      return;
    }
    if (q === lastFetchedRef.current) return;
    lastFetchedRef.current = q;

    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    setLoading(true);
    setUnavailable(false);

    try {
      const data = await api.get<{ suggestions: GeoSuggestion[]; attribution: string }>(
        `/locations/autocomplete?q=${encodeURIComponent(q)}&lang=${lang}`,
      );
      setSuggestions(data.suggestions ?? []);
      setAttribution(data.attribution ?? '© Geoapify');
      setOpen(true);
    } catch {
      setUnavailable(true);
      setSuggestions([]);
      setOpen(true);
    } finally {
      setLoading(false);
    }
  }, [lang]);

  const handleChange = useCallback((v: string) => {
    onQueryChange(v);
    // Reset search state but don't close until we have a result to show.
    if (v.length < MIN_LEN) {
      setSuggestions([]);
      setOpen(false);
      lastFetchedRef.current = '';
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchZone(v), 400);
  }, [fetchZone, onQueryChange]);

  const handleSelect = useCallback((s: GeoSuggestion) => {
    onSelect(s);
    setSuggestions([]);
    setOpen(false);
    lastFetchedRef.current = '';
  }, [onSelect]);

  const showList = open && query.length >= MIN_LEN;

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Input */}
      <Paper
        variant="outlined"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.75,
          borderRadius: 2,
          bgcolor: 'background.paper',
        }}
      >
        <SearchIcon sx={{ color: 'text.disabled', fontSize: 18 }} />
        <InputBase
          fullWidth
          value={query}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={placeholder}
          sx={{ fontSize: '0.875rem' }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onEnter?.();
            }
          }}
          onFocus={() => {
            if (suggestions.length > 0) setOpen(true);
          }}
          onBlur={() => {
            // Delay close so clicks on suggestions register first.
            setTimeout(() => setOpen(false), 150);
          }}
        />
        {loading && <CircularProgress size={14} sx={{ flexShrink: 0 }} />}
      </Paper>

      {/* Suggestions dropdown */}
      {showList && (
        <Paper
          elevation={4}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 20,
            mt: 0.5,
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {unavailable ? (
            <Typography variant="caption" sx={{ display: 'block', p: 1.5, color: 'text.secondary' }}>
              Recherche indisponible.
            </Typography>
          ) : suggestions.length === 0 ? (
            <Typography variant="caption" sx={{ display: 'block', p: 1.5, color: 'text.secondary' }}>
              Aucun lieu ne correspond.
            </Typography>
          ) : (
            <List dense disablePadding>
              {suggestions.map((s, i) => (
                <ListItemButton
                  key={i}
                  onMouseDown={(e) => e.preventDefault()} // prevent blur before click
                  onClick={() => handleSelect(s)}
                  sx={{ px: 1.5 }}
                >
                  <ListItemText
                    primary={s.label}
                    secondary={s.type}
                    primaryTypographyProps={{ fontSize: '0.8125rem' }}
                    secondaryTypographyProps={{ fontSize: '0.6875rem' }}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
          {/* Attribution — licence condition */}
          <Typography
            variant="caption"
            sx={{ display: 'block', textAlign: 'right', px: 1.5, py: 0.5, color: 'text.disabled', borderTop: '1px solid', borderColor: 'divider' }}
          >
            {attribution}
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
